import { Box, Button, Flex, Text } from "@chakra-ui/react";
import { Dispatch, FC, SetStateAction, useEffect } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { BsDashCircle, BsPlusCircle } from "react-icons/bs";
import PickListContent from "./PickListContent";
import { errorStyle } from "../../../utils/styles/styles";
import { errorColor, successColor } from "../../../utils/colors";

interface Option {
  code: string | number;
  name: string;
  description: string;
}

interface PickListProps {
  hasLoadedFeatures: boolean;
  featuresOptions: Option[];
  setFeaturesOptions: Dispatch<SetStateAction<Option[]>>;
  selectedFeaturesOptions: Option[];
  setSelectedFeaturesOptions: Dispatch<SetStateAction<Option[]>>;
  originalFeaturesDataTable: any[];
  originalSelectedFeaturesDataTable: any[];
  setOriginalFeaturesDataTable: (data: any) => void;
  setOriginalSelectedFeaturesDataTable: (data: any) => void;
  featuresTitle?: string;
  selectedFeaturesTitle?: string;
  isDisabled?: boolean;
  errors?: any;
  hasInteracted?: boolean;
  setHasInteracted: Dispatch<SetStateAction<boolean>>;
}

const PickList: FC<PickListProps> = ({
  hasLoadedFeatures = false,
  featuresOptions,
  setFeaturesOptions,
  selectedFeaturesOptions,
  setSelectedFeaturesOptions,
  originalFeaturesDataTable,
  originalSelectedFeaturesDataTable,
  setOriginalFeaturesDataTable,
  setOriginalSelectedFeaturesDataTable,
  featuresTitle,
  selectedFeaturesTitle,
  isDisabled,
  errors,
  hasInteracted,
  setHasInteracted,
}) => {
  const updateDataTables = (
    newFeaturesOptions: Option[],
    newSelectedFeaturesOptions: Option[],
  ) => {
    setFeaturesOptions(newFeaturesOptions);
    setSelectedFeaturesOptions(newSelectedFeaturesOptions);
    setHasInteracted(true);
  };

  const addAll = () => {
    const newSelectedFeaturesOptions = [
      ...selectedFeaturesOptions,
      ...featuresOptions,
    ];
    updateDataTables([], newSelectedFeaturesOptions);
    setOriginalSelectedFeaturesDataTable(newSelectedFeaturesOptions);
  };

  const removeAll = () => {
    const newFeaturesOptions = [...featuresOptions, ...selectedFeaturesOptions];
    updateDataTables(newFeaturesOptions, []);
    setOriginalFeaturesDataTable(newFeaturesOptions);
  };

  const addSelectedFeature = (item: Option) => {
    const newSelectedFeaturesOptions = [...selectedFeaturesOptions, item];
    const newFeaturesOptions = featuresOptions.filter(
      (feature) => feature.code !== item.code,
    );

    updateDataTables(newFeaturesOptions, newSelectedFeaturesOptions);
    setOriginalSelectedFeaturesDataTable(newSelectedFeaturesOptions);
  };

  const removeSelectedFeature = (item: Option) => {
    const newSelectedFeaturesOptions = selectedFeaturesOptions.filter(
      (feature) => feature.code !== item.code,
    );
    const newFeaturesOptions = [...featuresOptions, item];

    updateDataTables(newFeaturesOptions, newSelectedFeaturesOptions);
    setOriginalFeaturesDataTable(newFeaturesOptions);
  };

  useEffect(() => {
    if (hasInteracted) {
      const updatedOriginalFeatures = originalFeaturesDataTable.filter(
        (feature) =>
          !selectedFeaturesOptions.some(
            (selected) => selected.code === feature.code,
          ),
      );

      setOriginalFeaturesDataTable(updatedOriginalFeatures);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedFeaturesOptions]);

  useEffect(() => {
    if (hasInteracted) {
      const updatedSelectedOriginalFeatures =
        originalSelectedFeaturesDataTable.filter(
          (feature) =>
            !featuresOptions.some((selected) => selected.code === feature.code),
        );

      setOriginalSelectedFeaturesDataTable(updatedSelectedOriginalFeatures);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featuresOptions]);

  return (
    <Flex direction="row" justifyContent="flex-start" gap={4}>
      <Flex flex="1" direction="column">
        <Box>
          <PickListContent
            isLoaded={hasLoadedFeatures}
            features={featuresOptions}
            setFeatures={setFeaturesOptions}
            originalData={originalFeaturesDataTable}
            onFeatureClick={addSelectedFeature}
            placeholderText="Pesquisar privilégios..."
            iconComponent={BsPlusCircle}
            iconProps={{ boxSize: 5, color: successColor }}
            title={featuresTitle}
            isDisabled={isDisabled}
          />
        </Box>
      </Flex>
      <Flex direction="column" gap="4">
        <Button
          variant="secondary"
          rightIcon={<ChevronRightIcon />}
          onClick={addAll}
          isDisabled={isDisabled}
        >
          Adicionar todos
        </Button>
        <Button
          variant="secondary"
          leftIcon={<ChevronLeftIcon />}
          onClick={removeAll}
          isDisabled={isDisabled}
        >
          Remover todos
        </Button>
      </Flex>
      <Flex flex="1" direction="column">
        <Box
          {...(hasInteracted &&
          !!errors.permissions &&
          originalSelectedFeaturesDataTable.length === 0
            ? errorStyle
            : {})}
        >
          <PickListContent
            isLoaded={hasLoadedFeatures}
            features={selectedFeaturesOptions}
            setFeatures={setSelectedFeaturesOptions}
            originalData={originalSelectedFeaturesDataTable}
            onFeatureClick={removeSelectedFeature}
            placeholderText="Pesquisar privilégios..."
            iconComponent={BsDashCircle}
            iconProps={{ boxSize: 5, color: errorColor }}
            title={selectedFeaturesTitle}
            isDisabled={isDisabled}
          />
        </Box>
        {hasInteracted &&
          errors.permissions &&
          originalSelectedFeaturesDataTable.length === 0 && (
            <Text color={errorColor} fontSize="sm" mt={2}>
              {errors.permissions.message}
            </Text>
          )}
      </Flex>
    </Flex>
  );
};

export default PickList;
